import React, { useMemo } from 'react';
import type { ChannelType } from '../../types';
import MultiChannelSelect from './MultiChannelSelect';
import SingleChannelSelect from './SingleChannelSelect';
import './index.scss';
import { FilterButtonPoint } from '@op/operationLog/pages/filter';
import BubbleCommon from '@op/components/BubbleCommon';
import DBO_FWC from '@op/hooks/useFrontWebConfig';
import { channelSplitFlag } from '../../constants/channel';

const CHANNEL_TYPES = ['social', 'comments', 'news', 'security'] as const;

/**
 * 把分组配置（裸渠道名）拼成消费端期望的带前缀 value 数组。
 * Single: 只取 channelKey 对应一组；Multi: 展平全部四类。
 */
function buildExcludeOptions(
  grouped: unknown,
  channelKey?: 'social' | 'comments' | 'news',
): string[] {
  if (!grouped || typeof grouped !== 'object' || Array.isArray(grouped))
    return [];
  const keys = channelKey ? [channelKey] : CHANNEL_TYPES;
  const result: string[] = [];
  keys.forEach((type) => {
    const list = (grouped as Record<string, unknown>)[type];
    if (!Array.isArray(list)) return;
    list.forEach((item) => {
      if (typeof item !== 'string') return;
      result.push(`${type}${channelSplitFlag}${item.toLocaleLowerCase()}`);
    });
  });
  return result;
}

/** 全局配置产出项 + 页面级配置，并集去重。 */
function mergeExcludeOptions(
  globalList: string[],
  pageList?: string[],
): string[] {
  if (!pageList?.length) return globalList;
  return Array.from(new Set([...globalList, ...pageList]));
}

export default function ChannelSelect({
  channelKey,
  tips,
  firstExcludeOptions: pageFirstExcludeOptions,
  ...otherProps
}: ChannelType) {
  const mergedFirstExcludeOptions = useMemo(() => {
    const globalList = buildExcludeOptions(
      DBO_FWC.get('default_no_selected_channels'),
      channelKey,
    );
    return mergeExcludeOptions(globalList, pageFirstExcludeOptions);
  }, [channelKey, pageFirstExcludeOptions]);

  return (
    <div className="channel-select-wrapper">
      <BubbleCommon tipsMap={tips} showIcon={false} placement="top">
        {channelKey ? (
          <SingleChannelSelect
            {...otherProps}
            firstExcludeOptions={mergedFirstExcludeOptions}
            pointKey={FilterButtonPoint.channel}
            channelKey={channelKey}
          ></SingleChannelSelect>
        ) : (
          <MultiChannelSelect
            {...otherProps}
            firstExcludeOptions={mergedFirstExcludeOptions}
            pointKey={FilterButtonPoint.channel}
          ></MultiChannelSelect>
        )}
      </BubbleCommon>
    </div>
  );
}
